/**
 * WebGL2 renderer setup, tone mapping and quality application.
 * ARCHITECTURE.md §6, §9. Owned by src/render.
 *
 * Exposure and tone mapping live here and ONLY here. ARCHITECTURE.md §14.1 lists
 * tone mapping / lighting / exposure as a coupled concern that must have a single owner — if a
 * scene looks flat, the fix is measured here, not by crushing albedo in src/materials.
 */

import * as THREE from 'three';
import { QUALITY_PRESETS, clamp } from '../core/index.js';

/**
 * Base exposure. The lighting rig is authored in physical-ish units, so this is the single
 * knob that maps scene luminance into the tone mapper's sweet spot.
 */
const BASE_EXPOSURE = 1.15;

export function createRenderer(canvas, ctx) {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false, // AA is a quality-preset decision (FXAA/TAA), not an unconditional cost
    alpha: false,
    powerPreference: 'high-performance',
    stencil: false,
    depth: true,
    // Capture tooling renders one frame and then screenshots it from outside the rAF loop, so the
    // drawing buffer has to survive until the compositor reads it. Only enabled for capture runs
    // because preserving the buffer costs bandwidth on every frame.
    preserveDrawingBuffer: !!ctx.flags.capture,
  });

  const gl = renderer.getContext();
  if (!gl || typeof WebGL2RenderingContext === 'undefined' || !(gl instanceof WebGL2RenderingContext)) {
    throw new Error('Claude of Iron requires WebGL2.');
  }

  // HDR-ish pipeline: render linear, tone map with ACES on the way out.
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = BASE_EXPOSURE;

  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.shadowMap.autoUpdate = true;

  renderer.info.autoReset = false; // we reset per frame ourselves so draw calls are per-frame
  ctx.render.renderer = renderer;
  ctx.render.exposure = BASE_EXPOSURE;
  return renderer;
}

/**
 * Applies a quality preset. Never disables a defining visual feature — ARCHITECTURE.md §16
 * forbids hitting performance targets by turning off the things that make the game look like
 * itself. Only resolution, shadow detail and sample counts move.
 */
export function applyQuality(renderer, ctx, presetName) {
  const preset = QUALITY_PRESETS[presetName] || QUALITY_PRESETS.high;
  ctx.render.preset = presetName;
  renderer.shadowMap.type = preset.shadowMapSize >= 2048 ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap;
  renderer.shadowMap.needsUpdate = true;
  setRenderScale(renderer, ctx, preset.renderScale);
  return preset;
}

/**
 * Sets the internal render scale. Dynamic resolution moves this within
 * [preset * 0.7, preset] only, so a benchmark can never silently drop below the preset it claims.
 */
export function setRenderScale(renderer, ctx, scale) {
  const preset = QUALITY_PRESETS[ctx.render.preset] || QUALITY_PRESETS.high;
  const floor = preset.renderScale * 0.7;
  const clamped = clamp(scale, floor, preset.renderScale);
  ctx.render.renderScale = clamped;
  resize(renderer, ctx, ctx.render.width, ctx.render.height, ctx.render.dpr);
  return clamped;
}

export function resize(renderer, ctx, width, height, dpr) {
  // Never accept a degenerate target. A 0-sized drawing buffer renders nothing while reporting
  // no error, which is one of the hardest WebGL failures to notice.
  ctx.render.width = Math.max(1, Math.floor(width) || 1);
  ctx.render.height = Math.max(1, Math.floor(height) || 1);
  ctx.render.dpr = dpr > 0 ? dpr : 1;
  width = ctx.render.width;
  height = ctx.render.height;
  dpr = ctx.render.dpr;
  const scale = ctx.render.renderScale || 1;
  // setSize with updateStyle=false keeps the canvas CSS size fixed while the backing store
  // changes, which is exactly what a render scale is.
  // Three floors width * pixelRatio. Binary 0.7 * 720 can become 503.999999… and silently place
  // the backing buffer one pixel below the declared adaptive floor, so bias only that rounding
  // boundary upward; the epsilon is far too small to change any intended resolution tier.
  renderer.setPixelRatio(dpr * scale + 1e-7);
  renderer.setSize(width, height, false);
  if (ctx.render.camera) {
    ctx.render.camera.aspect = width / Math.max(1, height);
    ctx.render.camera.updateProjectionMatrix();
  }
}

/** Exposure bias from user settings, applied on top of the base exposure. */
export function applyExposureBias(renderer, ctx) {
  const bias = ctx.config.get('exposureBias') || 0;
  const exposure = BASE_EXPOSURE * Math.pow(2, bias);
  renderer.toneMappingExposure = exposure;
  ctx.render.exposure = exposure;
  return exposure;
}

/** Per-frame draw-call and triangle counts, for the dev HUD and tools/profile.mjs. */
export function readRendererInfo(renderer) {
  const info = renderer.info;
  return {
    drawCalls: info.render.calls,
    triangles: info.render.triangles,
    lines: info.render.lines,
    points: info.render.points,
    geometries: info.memory.geometries,
    textures: info.memory.textures,
    programs: info.programs ? info.programs.length : 0,
  };
}

export { BASE_EXPOSURE };
